// state of the entire module
import { GlobalState } from '../../../store/global';
import { createSelector } from '@ngrx/store';
import { aiAssistedRepliesAdapter, aiDefaultLanguagesAdapter, aiSettingsAdapter, replyTemplatesAdapter, reviewsAdapter, reviewTranslationsAdapter } from './review-adapters';
import { ReviewRequestParams } from '../../../models/review-request-params';
import { AiSettingsType, ReplyStyle } from '../reply-tab-group/ai-replies-settings/ai-replies-settings.component';
import { ReviewEdit, ReviewFilterNames, ReviewReply, ReviewType } from '../../../models/review-filters';
import { selectAiRepliesActivated, selectDisplayNames, selectFilteredLocationDataList, selectLocationDataLoading, selectUniqueMyBAccounts } from '../../../store/global/core-selectors';
import { filterGbpLocationIdsByBusinessNames, filterGbpLocationIdsByByGmbAccountIds, filterGbpLocationIdsByByStoreCodes, filterGbpLocationIdsByCities, filterGbpLocationIdsByCountryCodes, intersectionOfNonEmptyFilters } from '../../../store/global/location-data-utils';
import { MainRoutes } from '../../../core/enums';

export const selectReviewState = (state: GlobalState) => state.review;

// internal use only
export const selectReviewsState = createSelector(selectReviewState, (reviewState) => reviewState.reviews);
export const reviewsAdapterSelectors = reviewsAdapter.getSelectors();
export const selectReplyTemplatesState = createSelector(selectReviewState, (reviewState) => reviewState.replyTemplates);
export const replyTemplatesAdapterSelectors = replyTemplatesAdapter.getSelectors();
export const selectAiAssistedRepliesState = createSelector(selectReviewState, (reviewState) => reviewState.aiAssistedReplies);
export const aiAssistedRepliesSelectors = aiAssistedRepliesAdapter.getSelectors();
export const selectAiSettingsState = createSelector(selectReviewState, (reviewState) => reviewState.aiSettings);
export const aiSettingsAdapterSelectors = aiSettingsAdapter.getSelectors();

export const selectDefaultLanguagesState = createSelector(selectReviewState, (reviewState) => reviewState.defaultLanguages);
export const defaultLanguagesAdapterSelectors = aiDefaultLanguagesAdapter.getSelectors();


// reviews selectors
export const selectReviews = createSelector(selectReviewsState, reviewsAdapterSelectors.selectAll);
export const selectReview = (id: number) => createSelector(selectReviewsDictionary, reviewsDictionary => reviewsDictionary[id]);
export const selectReviewsDictionary = createSelector(selectReviewsState, reviewsAdapterSelectors.selectEntities);

// replay templates selectors
export const selectReplyTemplates = createSelector(selectReplyTemplatesState, replyTemplatesAdapterSelectors.selectAll);
export const selectReplyTemplate = (id: number) => createSelector(selectReplyTemplatesDictionary, replyTemplatesDictionary => replyTemplatesDictionary[id]);
export const selectReplyTemplatesDictionary = createSelector(selectReplyTemplatesState, replyTemplatesAdapterSelectors.selectEntities);

// ai-assisted replies
export const selectAiAssistedReplies = createSelector(selectAiAssistedRepliesState, aiAssistedRepliesSelectors.selectAll);
export const selectAiAssistedRepliesDictionary = createSelector(selectAiAssistedRepliesState, aiAssistedRepliesSelectors.selectEntities);
export const selectAiAssistedRepliesPerReview = (id: number) => createSelector(selectAiAssistedRepliesDictionary, aiAssistedRepliesDictionary => aiAssistedRepliesDictionary[id]);
export const selectAiAssistedRepliesPerReviewCount = (id: number) => createSelector(selectAiAssistedRepliesPerReview(id), aiAssistedRepliesPerReview => aiAssistedRepliesPerReview?.replies?.length);

// ai-settings
export const selectAiSettings = createSelector(selectAiSettingsState, aiSettingsAdapterSelectors.selectAll);
export const selectAiGreetings = createSelector(selectAiSettings, aiSettings => aiSettings.filter(aiSetting => aiSetting.type === AiSettingsType.GREETING));
export const selectAiClosings = createSelector(selectAiSettings, aiSettings => aiSettings.filter(aiSettings => aiSettings.type === AiSettingsType.CLOSING));
// there should only ever be 1 setting of type 'LANGUAGE'
export const selectAiLanguage = createSelector(selectAiSettings, aiSettings => aiSettings.find(aiSettings => aiSettings.type === AiSettingsType.LANGUAGE));
// text should be the language code in this case, i.e. 'EN', 'DE', etc.
export const selectAiReplyStyleSetting = createSelector(selectAiSettings, aiSettings => aiSettings.find(aiSettings => aiSettings.type === AiSettingsType.STYLE));
export const selectAiReplyStyle = createSelector(selectAiReplyStyleSetting, aiSettings => (aiSettings?.text as ReplyStyle));

export const selectAiLanguageCode = createSelector(selectAiLanguage, AiLanguage => {
  return AiLanguage?.text;
});
export const selectAiLanguageForReviewType = createSelector(selectAiLanguage, selectedAiLanguage => selectedAiLanguage?.reviewType);
// used as options for language dropdown
export const selectAiDefaultLanguages = createSelector(selectDefaultLanguagesState, defaultLanguagesAdapterSelectors.selectAll);
export const selectDefaultAiLanguageSelected = createSelector(selectAiDefaultLanguages, selectAiLanguage, (defaultLanguages, selectedAiLanguage) => defaultLanguages.find(languages => languages?.key === selectedAiLanguage?.text));

export const selectSelectedAiGreeting = createSelector(selectAiGreetings, aiSettings => aiSettings.find(aiSetting => aiSetting.selected) || null);
export const selectSelectedAiClosing = createSelector(selectAiClosings, aiSettings => aiSettings.find(aiSettings => aiSettings.selected) || null);

export const selectReviewFilters = createSelector(selectReviewState, (reviewState) => reviewState.filters);
export const selectReviewFiltersActive = createSelector(
    selectReviewFilters,
    (filters) => {
      if (filters) {
        const filterKeys: string[] = Object
            .keys(filters)
            .filter((key: string) => key !== ReviewFilterNames.SORT_TYPE); // don't count sortType as filter
        return filterKeys.some((key: string) => Array.isArray(filters[key]) ? !!filters[key].length : !!filters[key]);
      } else return false;
    },
);

export const selectReviewPage = createSelector(selectReviewState, (reviewState) => reviewState.pageIndex + 1);
export const selectPaginatorData = createSelector(selectReviewState, (reviewState) =>
    ({ totalNumber: reviewState.totalNumber, pageIndex: reviewState.pageIndex, pageSize: reviewState.pageSize}));

export const selectGbpLocationIdsFromFilters = createSelector(
    selectFilteredLocationDataList(MainRoutes.REVIEWS), selectReviewFilters, (locationDataList, filters) => {
      return intersectionOfNonEmptyFilters([
        filterGbpLocationIdsByCountryCodes(locationDataList, filters?.countryCodes),
        filterGbpLocationIdsByByStoreCodes(locationDataList, filters?.storeCodes),
        filterGbpLocationIdsByBusinessNames(locationDataList, filters?.businessNames),
        filterGbpLocationIdsByCities(locationDataList, filters?.cities),
        filterGbpLocationIdsByByGmbAccountIds(locationDataList, filters?.gbpAccountIds),
      ]);
    });

export const selectReviewRequestParams = createSelector(
    selectReviewState,
    selectReviewPage,
    selectGbpLocationIdsFromFilters,
    (reviewState, reviewPage, gbpLocationIds): ReviewRequestParams => ({
      pageSize: reviewState.pageSize,
      requestedPage: reviewPage,
      gbpLocationIds: gbpLocationIds,
      from: reviewState.filters?.from,
      to: reviewState.filters?.to,
      sortType: reviewState.filters?.sortType,
      replied: reviewState.filters?.reply === ReviewReply.REPLIED ?
          true : reviewState.filters?.reply === ReviewReply.UNREPLIED ?
              false :
              null,
      edited: reviewState.filters?.edit === ReviewEdit.EDITED ?
          true : reviewState.filters?.edit === ReviewEdit.UNEDITED ?
              false :
              null,
      hasText: reviewState.filters?.reviewType === ReviewType.WITH_TEXT ?
          true : reviewState.filters?.reviewType === ReviewType.RATING_ONLY ?
              false :
              null,
      starRatings: reviewState.filters?.rating?.length ? reviewState.filters?.rating : null,
    }));

export const selectReviewChipsData = createSelector(selectReviewFiltersActive,
    selectReviewFilters,
    selectUniqueMyBAccounts(MainRoutes.REVIEWS),
    selectDisplayNames,
    (reviewFiltersActive,
     reviewFilters,
     uniqueMyBAccounts,
     displayNames) => ({ reviewFiltersActive, reviewFilters, uniqueMyBAccounts, displayNames }));

export const selectInvalidListingsFilterSelection = createSelector(selectGbpLocationIdsFromFilters, (ids) => {
  return ids === null;
});

export const selectReviewViewModel = createSelector(selectReviewState,
    selectReviewPage,
    selectReviews,
    selectReplyTemplates,
    selectAiRepliesActivated,
    selectInvalidListingsFilterSelection,
    selectReviewFiltersActive,
    selectLocationDataLoading,
    (reviewState,
     page,
     reviews,
     replyTemplates,
     aiRepliesActivated,
     invalidListingsFilterSelection,
     reviewFiltersActive,
     locationDataLoading,
    ) => ({
      filters: reviewState.filters,
      reviewFiltersActive,
      locationDataLoading,
      invalidListingsFilterSelection,
      reviews,
      replyTemplates,
      aiRepliesActivated,
      from: reviewState.from,
      to: reviewState.to,
      page,
      pageIndex: reviewState.pageIndex,
      pageSize: reviewState.pageSize,
      length: reviewState.totalNumber,
    }));

export const selectReviewPageSize = createSelector(selectReviewState, (reviewState) => reviewState.pageSize);
export const selectReviewTotalNumber = createSelector(selectReviewState, (reviewState) => reviewState.totalNumber);

export const selectReviewTranslationsState = createSelector(selectReviewState, (reviewState) => reviewState.reviewTranslations);
export const reviewTranslationsAdapterSelectors = reviewTranslationsAdapter.getSelectors();

export const selectReviewTranslations = createSelector(selectReviewTranslationsState, reviewTranslationsAdapterSelectors.selectAll);
export const selectReviewTranslationsDictionary = createSelector(selectReviewTranslationsState, reviewTranslationsAdapterSelectors.selectEntities);
export const selectReviewTranslation = (reviewId: number, languageCode: string) => createSelector(selectReviewTranslationsDictionary, (reviewTranslationsDictionary) =>
    reviewTranslationsDictionary[`${reviewId}_${languageCode}`]);


